import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import { TResponse, TResponseWithQData } from "../../../types/common";
import { IAccountantPayloadType } from "@src/types/api/accontant-types";
import { accountantList, createAccountant, detailAccountant, updateAccountant } from "./callbacks";
import { detailUser } from "../user-table/callbacks";

export const UseAccountantSave = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IAccountantPayloadType>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IAccountantPayloadType>,
    unknown,
    IAccountantPayloadType
  > = {}
): UseMutationResult<TResponse<IAccountantPayloadType>, unknown, IAccountantPayloadType> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IAccountantPayloadType>, unknown, IAccountantPayloadType>({
    mutationFn: async (data: IAccountantPayloadType) => {
      const response = await createAccountant(data);
      return response.data;
    },
    onSuccess: (res: any, ...rest) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.ACCOUNTANT_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return;
          const exists = old.data.find((v: any) => v._id === res.data._id);
          return {
            ...old,
            data: exists
              ? old.data.map((v: any) =>
                  v._id === res.data._id ? res.data : v
                )
              : [res.data, ...old.data],
          };
        }
      );

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

export const UseAccountantUpdate = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IAccountantPayloadType>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IAccountantPayloadType>,
    unknown,
    IAccountantPayloadType
  > = {}
): UseMutationResult<TResponse<IAccountantPayloadType>, unknown, IAccountantPayloadType> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IAccountantPayloadType>, unknown, IAccountantPayloadType>({
    mutationFn: async (data: IAccountantPayloadType) => {
      const response = await updateAccountant(data);
      return response.data;
    },
    onSuccess: (res: any, ...rest) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.ACCOUNTANT_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return;
          const exists = old.data.find((v: any) => v._id === res.data._id);
          return {
            ...old,
            data: exists
              ? old.data.map((v: any) =>
                  v._id === res.data._id ? res.data : v
                )
              : [res.data, ...old.data],
          };
        }
      );

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

export const useAccountantList = (
  qData: QData,
  options?: Omit<
    UseQueryOptions<
      TResponseWithQData<IAccountantPayloadType[]>,
      Error
    >,
    "queryKey" | "queryFn"
  >
): UseQueryResult<
  TResponseWithQData<IAccountantPayloadType[]>,
  Error
> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<
    TResponseWithQData<IAccountantPayloadType[]>,
    Error
  >({
    queryKey: [
      API_ENDPOINTS.ACCOUNTANT_LIST,
      { ...queryParams, total: null },
    ],
    queryFn: async () => {
      const res = await accountantList(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map((x) => ({ key: x._id, ...x })),
      qData: { ...queryParams, total: res.extra?.total },
    }),
    staleTime: 300000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams((prev: any) => ({ ...prev, ...params }));
    },
  };
};

export const useAccountantDetail   = (
  id: string,
  options?: Omit<
    UseQueryOptions<TResponse<IAccountantPayloadType>, Error>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<TResponse<IAccountantPayloadType>, Error> => {
  return useQuery<TResponse<IAccountantPayloadType>, Error>({
    queryKey: [API_ENDPOINTS.ACCOUNTANT_DETAIL, id],
    queryFn: async () => {
      const res = await detailAccountant(id);
      return res.data;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 300000,
    ...options,
  });
};

// export const useServiceDelete = (
//   { qData = { page: 1, limit: 20 }, onSuccess, onError }: {
//     qData?: QData;
//     onSuccess?: (res: TResponse<IDeleteDataResponseAndPayload>) => void;
//     onError?: (error: unknown) => void;
//   }
//   ,
//   options: UseMutationOptions<TResponse<IDeleteDataResponseAndPayload>, unknown, IDeleteDataResponseAndPayload> = {}
// ): UseMutationResult<TResponse<IDeleteDataResponseAndPayload>, unknown, IDeleteDataResponseAndPayload> => {
//   const queryClient = useQueryClient();

//   return useMutation<TResponse<IDeleteDataResponseAndPayload>, unknown, IDeleteDataResponseAndPayload>({
//     mutationFn: async (data: IDeleteDataResponseAndPayload) => {
//       const response = await deleteService(data);
//       return response.data;
//     },
//     onSuccess: (res: TResponse<IDeleteDataResponseAndPayload>, ...rest) => {
//       console.log(API_ENDPOINTS.SERVICE_LIST, { ...qData, total: null })
//       console.log(res,"res-delete")
//       queryClient.setQueryData([API_ENDPOINTS.SERVICE_LIST, { ...qData, total: null }], (old: TResponse<IDeleteDataResponseAndPayload[]>) => {
//         if (!old) return;
//         return {
//           ...old,
//           data: Array.isArray(res.data.ids) ? old.data.filter((v: any) => ~res.data.ids.includes(v._id)) : old.data.filter((v: any) => res.data.ids !== v._id),
//         };
//       });

//       onSuccess?.(res);
//       options?.onSuccess?.(res, ...rest);
//     },
//     onError: (error, ...rest) => {
//       onError?.(error);
//       options?.onError?.(error, ...rest);
//     },
//     ...options
//   });
// };
